import React, { useState } from 'react';
import DynamicProgress from './DynamicProgress';
import AmbassadorDonorsModal from './AmbassadorDonorsModal';
import { evalFormula } from '../formula';

/**
 * Personal ambassador meter — reads clearly as one individual's progress even
 * when the campaign's own meter sits on the same page. Shares the campaign's
 * brand color (accent) so the two feel like one system, while the framing keeps
 * the personal one distinct. Several visual variants (see AMB_CARD_VARIANTS).
 *
 * SIZE: everything (meter, text, avatar, padding) scales by a single factor `k`,
 * derived from a manual size slider (`props.sizeScale`) multiplied by the block's
 * width relative to its default (`blockWidth`/`baseWidth`) — so resizing the
 * block narrower, or dragging the size slider down, shrinks meter AND text
 * together. k = 1 reproduces the previous fixed sizes.
 *
 * Shared by the live renderer (PublicCampaign) and the builder preview
 * (BlockRenderer) so the two never drift.
 */

// `h` = representative thumbnail height (px) in the editor picker, so the
// selected blue frame hugs each option's real proportions instead of a uniform
// box (compact styles are short, hero is tall).
export const AMB_CARD_VARIANTS: { value: string; label: string; short: string; h: number }[] = [
  { value: 'card',      label: 'כרטיס רך (ברירת מחדל)',     short: 'כרטיס רך',  h: 56 },
  { value: 'spotlight', label: 'זרקור — רקע צבעוני בולט',    short: 'זרקור',     h: 56 },
  { value: 'stats',     label: 'נתונים — אריחי סטטיסטיקה',   short: 'נתונים',    h: 56 },
  { value: 'hero',      label: 'מרכזי — ממורכז ובולט',       short: 'מרכזי',     h: 74 },
  { value: 'circle',    label: 'עיגול — מד מעגלי',            short: 'עיגול',     h: 48 },
  { value: 'glass',     label: 'זכוכית — שקוף ומטושטש',       short: 'זכוכית',    h: 58 },
  { value: 'ribbon',    label: 'סרט — פס צבע בצד',            short: 'סרט',       h: 54 },
  { value: 'dark',      label: 'כהה — רקע כהה מודגש',         short: 'כהה',       h: 56 },
  { value: 'pill',      label: 'גלולה — פס מעוגל קומפקטי',    short: 'גלולה',     h: 42 },
  { value: 'compact',   label: 'קומפקטי — שורה אחת',          short: 'קומפקטי',   h: 40 },
  { value: 'strip',     label: 'רצועה — שורה דקה במיוחד',     short: 'רצועה',     h: 30 },
  { value: 'mini',      label: 'מיני — מד זעיר בלבד',          short: 'מיני',      h: 34 },
  { value: 'outline',   label: 'קו מתאר — נקי ומינימלי',      short: 'קו מתאר',   h: 54 },
  { value: 'plain',     label: 'פשוט — מד בלבד',              short: 'פשוט',      h: 46 },
];

/** hex (#rgb / #rrggbb) → rgba() at alpha. Falls back to the raw value for non-hex. */
function hexA(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
/** Darken a #rrggbb color toward black by `amt` (0..1). Used for the spotlight gradient. */
function darken(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amt));
  const g = Math.round(((n >> 8) & 255) * (1 - amt));
  const b = Math.round((n & 255) * (1 - amt));
  return `rgb(${r}, ${g}, ${b})`;
}

export interface AmbassadorStatsCardProps {
  p: any;                 // block.props
  s: any;                 // block.style
  baseStyle: React.CSSProperties;
  accent: string;         // brand / campaign primary
  name: string;
  raised: number;
  goal: number;
  donors: number;
  /** Standing-order count for this ambassador — drives the 'campaign_recurring'
   *  data source and the recurringCount term in formulas. */
  recurring?: number;
  activeCurrency?: any;
  animate?: boolean;
  editLocale?: any;
  scale?: number;         // builder-canvas zoom scale (unrelated to size factor)
  sample?: boolean;       // builder preview → append the "only on ambassador link" hint
  // When both are present the donor-count chip becomes a button that opens the
  // per-ambassador donor list. Absent in the builder preview (no live data).
  campaignSlug?: string;
  ambassadorSlug?: string;
  // Width-driven auto-sizing: the block's current width and its default width
  // for the device. blockWidth/baseWidth ≈ 1 at the authored default, <1 when
  // the block is resized narrower — feeds the size factor so content shrinks.
  blockWidth?: number;
  baseWidth?: number;
}

const AmbassadorStatsCard: React.FC<AmbassadorStatsCardProps> = ({
  p, s, baseStyle, accent, name, raised, goal, donors, recurring,
  activeCurrency, animate, editLocale, scale, sample,
  campaignSlug, ambassadorSlug, blockWidth, baseWidth,
}) => {
  const [donorsOpen, setDonorsOpen] = useState(false);
  const acc = p.accentColor || accent || '#2563eb';
  const variant: string = AMB_CARD_VARIANTS.some(v => v.value === p.cardVariant) ? p.cardVariant : 'card';
  const showName = p.showName !== false;
  const showDonors = p.showDonors !== false;
  const showAvatar = p.showAvatar !== false && variant !== 'plain' && variant !== 'mini';
  const subtitle = p.subtitle ?? 'שגריר בקמפיין';

  // ── Size factor ─────────────────────────────────────────────────────────
  // Manual slider × width ratio, clamped. z(n) scales any px value by it.
  const manualScale = Math.max(0.3, Math.min(1.4, Number(p.sizeScale) || 1));
  const autoScale = (blockWidth && baseWidth && baseWidth > 0)
    ? Math.max(0.5, Math.min(1.12, blockWidth / baseWidth))
    : 1;
  const k = Math.max(0.3, Math.min(1.4, manualScale * autoScale));
  const z = (n: number) => Math.round(n * k * 100) / 100;

  const nameLine = String(p.nameTemplate || 'היעד של {name}').replace('{name}', name);
  const donorsLine = String(p.donorsTemplate || '{count} תורמים').replace('{count}', Number(donors || 0).toLocaleString());
  const txtColor = (s.textColor as string) || (baseStyle.color as string) || '#1f2937';
  const initial = (name || '').trim().charAt(0) || '🤝';
  // '' = author chose "no symbol" (honor it); undefined = never set → default ₪.
  const sym = p.currency === '' ? '' : (p.currency || '₪');
  const fmt = (n: number) => `${sym}${Number(n || 0).toLocaleString()}`;

  // Coordinate the bar with the accent by default; a bar color the owner
  // explicitly changed away from the shared default still wins.
  const barColor = p.barColor && p.barColor !== '#2563eb' ? p.barColor : acc;

  // ── Data source ─────────────────────────────────────────────────────────
  // Mirrors the progress block: the meter can measure the ambassador's raised
  // amount (money, default), their standing-order count, or a custom formula
  // over raised/donationCount/recurringCount — all scoped to THIS ambassador.
  const dataSource: string = p.dataSource || 'campaign';
  const metricInputs = { raised: raised || 0, donationCount: Number(donors) || 0, recurringCount: Number(recurring) || 0, goal: goal || 0 };
  // Target can be a plain number (per source) OR a formula over the same live
  // metrics, when `targetMode === 'formula'` — a dynamic goal.
  const formulaTarget = () => {
    let t = 0;
    try { t = evalFormula(p.targetFormula, metricInputs); } catch { /* keep 0 */ }
    return t;
  };
  const metric = (() => {
    const tgt = Number(p.target) || 0;
    const isFormulaTarget = p.targetMode === 'formula';
    if (dataSource === 'campaign_recurring') {
      return { current: Number(recurring) || 0, target: isFormulaTarget ? formulaTarget() : tgt, isMoney: false, label: 'הוראות קבע' };
    }
    if (dataSource === 'formula') {
      let cur = 0;
      try { cur = evalFormula(p.formula, metricInputs); } catch { /* keep 0 */ }
      return { current: cur, target: isFormulaTarget ? formulaTarget() : tgt, isMoney: false, label: p.label || 'התקדמות' };
    }
    if (dataSource === 'static') {
      return { current: Number(p.current) || 0, target: isFormulaTarget ? formulaTarget() : tgt, isMoney: false, label: p.label || '' };
    }
    // 'campaign' (default) — money raised vs the ambassador's personal goal
    // (a manual target override, or a formula target, still wins).
    const campTarget = isFormulaTarget ? formulaTarget() : (tgt > 0 ? tgt : (goal || 0));
    return { current: raised || 0, target: campTarget, isMoney: true, label: 'גויסו' };
  })();
  const fmtVal = (n: number) => metric.isMoney ? fmt(n) : Number(n || 0).toLocaleString();
  const showTarget = p.showTarget !== false && metric.target > 0;

  // meter — DynamicProgress. `scale` scales its fonts/circle; bar thickness is
  // scaled here (DynamicProgress doesn't scale thickness itself). All the
  // ambassador's metrics flow in via campaignData so every data source resolves.
  const meter = (over: any = {}, styleOver?: any, baseOver?: any) => {
    const bt = ((over.barThickness ?? p.barThickness ?? 16) as number) * k;
    const { barThickness: _omit, ...rest } = over;
    return (
      <DynamicProgress
        props={{ ...p, dataSource, showTarget, barColor, ...rest, barThickness: bt }}
        style={{ ...s, ...styleOver }}
        baseStyle={{ ...baseStyle, ...baseOver }}
        scale={(scale || 1) * k}
        campaignData={{ raised, goal, donationCount: Number(donors) || 0, recurringCount: Number(recurring) || 0 }}
        activeCurrency={activeCurrency}
        editLocale={editLocale}
        animateValue={animate}
        playEntrance
      />
    );
  };

  const avatar = (size: number, bg: string, fg: string, ring?: string) => {
    const d = z(size);
    return (
      <div style={{
        flex: '0 0 auto', width: d, height: d, borderRadius: '50%', background: bg, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: Math.round(d * 0.43),
        boxShadow: ring || `0 2px 8px ${hexA(acc, 0.4)}`,
      }}>{initial}</div>
    );
  };

  const nameBlock = (color: string, subColor: string, big?: boolean) => (
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: z(big ? 19 : (s.fontSize ? Number(s.fontSize) + 1 : 17)), color, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {nameLine}
      </div>
      {subtitle && <div style={{ fontSize: z(12.5), color: subColor, marginTop: z(2) }}>{subtitle}</div>}
    </div>
  );

  // The donor chip becomes a button (opens the donor list) only on the live page
  // where we have the slugs and at least one donor to show.
  const donorsClickable = showDonors && Number(donors) > 0 && !!campaignSlug && !!ambassadorSlug;
  const openDonors = () => setDonorsOpen(true);

  const donorsModal = (donorsOpen && donorsClickable) ? (
    <AmbassadorDonorsModal
      campaignSlug={campaignSlug!}
      ambassadorSlug={ambassadorSlug!}
      ambName={name}
      accent={acc}
      currencySymbol={sym}
      onClose={() => setDonorsOpen(false)}
    />
  ) : null;

  const donorChip = (bg: string, color: string, justify: string = 'flex-start') => {
    if (!showDonors) return null;
    const chipStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: z(6), padding: `${z(5)}px ${z(12)}px`, borderRadius: 999, background: bg, color, fontWeight: 600, fontSize: z(13), lineHeight: 1.4, fontFamily: 'inherit' };
    const inner = (
      <>
        <span aria-hidden>👥</span>{donorsLine}
        {donorsClickable && <span aria-hidden style={{ opacity: 0.75, marginInlineStart: z(2), fontSize: z(15) }}>›</span>}
      </>
    );
    return (
      <div style={{ display: 'flex', justifyContent: justify, marginTop: z(12) }}>
        {donorsClickable ? (
          <button type="button" onClick={openDonors} title="לחצו לרשימת התורמים" style={{ ...chipStyle, border: 'none', cursor: 'pointer' }}>{inner}</button>
        ) : (
          <span style={chipStyle}>{inner}</span>
        )}
        {donorsModal}
      </div>
    );
  };

  const sampleNote = sample ? (
    <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: z(10) }}>
      מוצג רק בקישור של שגריר · נתונים לדוגמה
    </div>
  ) : null;

  const pct = metric.target > 0 ? Math.round((metric.current / metric.target) * 100) : 0;
  // Small inline "👥 N" button used by compact/pill/strip/mini.
  const donorsInline = (color: string, fontSize: number, withChevron = false) => showDonors ? (
    donorsClickable ? (
      <button type="button" onClick={openDonors} title="לחצו לרשימת התורמים" style={{ border: 'none', background: 'none', padding: 0, margin: 0, cursor: 'pointer', color, fontWeight: 600, fontSize: z(fontSize), fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }}>👥 {Number(donors || 0).toLocaleString()}{withChevron ? ' ›' : ''}</button>
    ) : (
      <span style={{ color, fontSize: z(fontSize), fontVariantNumeric: 'tabular-nums' }}>👥 {Number(donors || 0).toLocaleString()}</span>
    )
  ) : null;

  // ── plain: minimal, centered — meter only ──────────────────────────────
  if (variant === 'plain') {
    return (
      <div style={{ width: '100%' }}>
        {showName && name && (
          <div style={{ textAlign: (s.textAlign as any) || 'center', fontWeight: 700, fontSize: z(s.fontSize ? Number(s.fontSize) + 2 : 18), color: txtColor, marginBottom: z(8) }}>{nameLine}</div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.12), acc, 'center')}
        {sampleNote}
      </div>
    );
  }

  // ── spotlight: filled brand gradient, white content ────────────────────
  if (variant === 'spotlight') {
    return (
      <div style={{
        width: '100%', boxSizing: 'border-box', color: '#fff',
        background: `linear-gradient(135deg, ${acc}, ${darken(acc, 0.28)})`,
        borderRadius: z(s.borderRadius ?? 20), padding: z(s.padding ?? 20),
        boxShadow: `0 10px 30px ${hexA(acc, 0.35)}`,
      }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(16) }}>
            {showAvatar && avatar(52, 'rgba(255,255,255,0.18)', '#fff', '0 0 0 2px rgba(255,255,255,0.35)')}
            {nameBlock('#fff', 'rgba(255,255,255,0.85)', true)}
          </div>
        )}
        {meter(
          { barColor: '#ffffff', barTrackColor: 'rgba(255,255,255,0.25)', pctColor: '#ffffff' },
          { textColor: '#ffffff' },
          { color: '#ffffff' },
        )}
        {donorChip('rgba(255,255,255,0.2)', '#fff')}
        {sampleNote}
      </div>
    );
  }

  // ── stats: numbers-forward — three stat tiles above a thin bar ─────────
  if (variant === 'stats') {
    const tile = (label: string, value: string, strong?: boolean, onClick?: () => void) => {
      const tileStyle: React.CSSProperties = { flex: 1, minWidth: 0, textAlign: 'center', padding: `${z(10)}px ${z(6)}px`, borderRadius: z(12), background: hexA(acc, strong ? 0.12 : 0.055), border: `1px solid ${hexA(acc, 0.14)}`, fontFamily: 'inherit' };
      const content = (
        <>
          <div style={{ fontWeight: 800, fontSize: z(18), color: (strong || onClick) ? acc : txtColor, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}{onClick && ' ›'}</div>
          <div style={{ fontSize: z(11.5), color: hexA(txtColor, 0.6), marginTop: z(2) }}>{label}</div>
        </>
      );
      return onClick
        ? <button type="button" onClick={onClick} title="לחצו לרשימת התורמים" style={{ ...tileStyle, cursor: 'pointer' }}>{content}</button>
        : <div style={tileStyle}>{content}</div>;
    };
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', background: s.bgTransparent === false ? (s.bgColor || '#fff') : '#fff', border: `1px solid ${hexA(acc, 0.18)}`, borderRadius: z(s.borderRadius ?? 18), padding: z(s.padding ?? 18), boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
            {showAvatar && avatar(44, acc, '#fff')}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
          </div>
        )}
        <div style={{ display: 'flex', gap: z(8), marginBottom: z(14) }}>
          {tile(metric.label || 'גויסו', fmtVal(metric.current), true)}
          {showTarget && tile('היעד', fmtVal(metric.target))}
          {showDonors && tile('תורמים', Number(donors || 0).toLocaleString(), false, donorsClickable ? openDonors : undefined)}
        </div>
        {meter({ barThickness: p.barThickness || 12, showPercent: true })}
        {donorsModal}
      </div>
    );
  }

  // ── compact: single horizontal row ─────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: z(12), background: s.bgTransparent === false ? (s.bgColor || '#fff') : hexA(acc, 0.05), border: `1px solid ${hexA(acc, 0.18)}`, borderRadius: z(s.borderRadius ?? 14), padding: z(s.padding ?? 12) }}>
        {showAvatar && avatar(40, acc, '#fff')}
        <div style={{ flex: 1, minWidth: 0 }}>
          {showName && name && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: z(8), marginBottom: z(6) }}>
              <span style={{ fontWeight: 700, fontSize: z(14.5), color: txtColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameLine}</span>
              <span style={{ fontWeight: 800, fontSize: z(13.5), color: acc, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{pct}%</span>
            </div>
          )}
          {meter({ barThickness: p.barThickness || 10, showPercent: false, showTarget: false, label: '' })}
          {showDonors && (
            <div style={{ fontSize: z(12), color: hexA(txtColor, 0.7), marginTop: z(6) }}>
              {fmtVal(metric.current)}{showTarget ? ` מתוך ${fmtVal(metric.target)}` : ''} · {donorsInline(acc, 12, true)}
              {donorsModal}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── outline: clean bordered card, no fill ──────────────────────────────
  if (variant === 'outline') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', background: s.bgTransparent === false ? (s.bgColor || 'transparent') : 'transparent', border: `1.5px solid ${hexA(acc, 0.5)}`, borderRadius: z(s.borderRadius ?? 16), padding: z(s.padding ?? 18) }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
            {showAvatar && avatar(44, 'transparent', acc, `inset 0 0 0 2px ${acc}`)}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
          </div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.1), acc)}
        {sampleNote}
      </div>
    );
  }

  // ── hero: centered, celebratory — big raised number focal ──────────────
  if (variant === 'hero') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center', background: s.bgTransparent === false ? (s.bgColor || '#fff') : `linear-gradient(180deg, ${hexA(acc, 0.07)}, ${hexA(acc, 0.015)})`, border: `1px solid ${hexA(acc, 0.2)}`, borderRadius: z(s.borderRadius ?? 20), padding: z(s.padding ?? 22), boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
        {showAvatar && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: z(10) }}>{avatar(60, acc, '#fff')}</div>}
        {showName && name && <div style={{ fontWeight: 800, fontSize: z(19), color: txtColor, lineHeight: 1.25 }}>{nameLine}</div>}
        {showName && subtitle && <div style={{ fontSize: z(12.5), color: hexA(txtColor, 0.6), marginTop: z(2) }}>{subtitle}</div>}
        <div style={{ fontWeight: 800, fontSize: z(30), color: acc, margin: `${z(14)}px 0 ${z(12)}px`, fontVariantNumeric: 'tabular-nums' }}>
          {fmtVal(metric.current)}{showTarget && <span style={{ fontSize: z(14), color: hexA(txtColor, 0.55), fontWeight: 600 }}> / {fmtVal(metric.target)}</span>}
        </div>
        {meter({ showAmount: false, showTarget: false, label: '' })}
        {donorChip(hexA(acc, 0.12), acc, 'center')}
        {sampleNote}
      </div>
    );
  }

  // ── circle: circular gauge beside the name/amounts ─────────────────────
  if (variant === 'circle') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: z(14), background: s.bgTransparent === false ? (s.bgColor || '#fff') : '#fff', border: `1px solid ${hexA(acc, 0.18)}`, borderRadius: z(s.borderRadius ?? 18), padding: z(s.padding ?? 18), boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
        <div style={{ flex: '0 0 auto', width: z(142), display: 'flex', justifyContent: 'center' }}>
          {meter({ display: 'circle', showAmount: false, showTarget: false }, undefined, undefined)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {showName && name && <div style={{ fontWeight: 700, fontSize: z(16), color: txtColor, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameLine}</div>}
          {showName && subtitle && <div style={{ fontSize: z(12), color: hexA(txtColor, 0.6), marginTop: z(2) }}>{subtitle}</div>}
          <div style={{ fontSize: z(14), color: txtColor, marginTop: z(8), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {fmtVal(metric.current)}{showTarget && <span style={{ color: hexA(txtColor, 0.55), fontWeight: 400 }}> / {fmtVal(metric.target)}</span>}
          </div>
          {donorChip(hexA(acc, 0.12), acc)}
        </div>
        {sampleNote}
      </div>
    );
  }

  // ── glass: frosted translucent panel (shines over image backgrounds) ───
  if (variant === 'glass') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.65)', borderRadius: z(s.borderRadius ?? 18), padding: z(s.padding ?? 18), boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
            {showAvatar && avatar(44, acc, '#fff')}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
          </div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.14), acc)}
        {sampleNote}
      </div>
    );
  }

  // ── ribbon: white card with an accent side strip + a שגריר badge ───────
  if (variant === 'ribbon') {
    const pad = z(s.padding ?? 18);
    return (
      <div style={{ position: 'relative', overflow: 'hidden', width: '100%', boxSizing: 'border-box', background: s.bgTransparent === false ? (s.bgColor || '#fff') : '#fff', border: `1px solid ${hexA(acc, 0.16)}`, borderRadius: z(s.borderRadius ?? 16), padding: pad, paddingInlineStart: pad + z(8), boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
        <div style={{ position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, width: z(6), background: `linear-gradient(180deg, ${acc}, ${hexA(acc, 0.7)})` }} />
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
            {showAvatar && avatar(44, acc, '#fff')}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
            <span style={{ flex: '0 0 auto', fontSize: z(11), fontWeight: 700, color: acc, background: hexA(acc, 0.12), padding: `${z(3)}px ${z(10)}px`, borderRadius: 999 }}>🤝 שגריר</span>
          </div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.12), acc)}
        {sampleNote}
      </div>
    );
  }

  // ── dark: dark charcoal card, accent bar — a themed contrast ──────────
  if (variant === 'dark') {
    const dk = '#1f2937';
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', color: '#fff', background: s.bgTransparent === false ? (s.bgColor || dk) : dk, border: `1px solid ${hexA(acc, 0.45)}`, borderRadius: z(s.borderRadius ?? 18), padding: z(s.padding ?? 18), boxShadow: '0 10px 28px rgba(0,0,0,0.28)' }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
            {showAvatar && avatar(44, acc, '#fff')}
            {nameBlock('#fff', 'rgba(255,255,255,0.6)')}
          </div>
        )}
        {meter(
          { barColor: acc, barTrackColor: 'rgba(255,255,255,0.14)', pctColor: '#fff' },
          { textColor: '#fff' },
          { color: '#fff' },
        )}
        {donorChip(hexA(acc, 0.28), '#fff')}
        {sampleNote}
      </div>
    );
  }

  // ── pill: compact fully-rounded bar, space-efficient ───────────────────
  if (variant === 'pill') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: z(12), background: hexA(acc, 0.08), border: `1px solid ${hexA(acc, 0.22)}`, borderRadius: 999, padding: `${z(8)}px ${z(14)}px` }}>
        {showAvatar && avatar(36, acc, '#fff')}
        <div style={{ flex: 1, minWidth: 0 }}>
          {showName && name && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: z(8), marginBottom: z(5) }}>
              <span style={{ fontWeight: 700, fontSize: z(13.5), color: txtColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameLine}</span>
              <span style={{ fontWeight: 800, fontSize: z(12.5), color: acc, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{pct}%</span>
            </div>
          )}
          {meter({ barThickness: p.barThickness || 8, showAmount: false, showTarget: false, showPercent: false, label: '' })}
        </div>
        {showDonors && (
          <span style={{ flex: '0 0 auto' }}>
            {donorsClickable
              ? <button type="button" onClick={openDonors} title="לחצו לרשימת התורמים" style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: acc, fontWeight: 700, fontSize: z(12.5), fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }}>👥 {Number(donors || 0).toLocaleString()} ›</button>
              : <span style={{ color: acc, fontWeight: 700, fontSize: z(12.5), fontVariantNumeric: 'tabular-nums' }}>👥 {Number(donors || 0).toLocaleString()}</span>}
          </span>
        )}
        {donorsModal}
      </div>
    );
  }

  // ── strip: ultra-thin single line, minimal footprint ───────────────────
  if (variant === 'strip') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: z(10), padding: z(s.padding ?? 6), color: txtColor }}>
        {showAvatar && avatar(24, acc, '#fff')}
        {showName && name && (
          <span style={{ fontWeight: 700, fontSize: z(13), color: txtColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '0 1 auto', maxWidth: '42%' }}>{name}</span>
        )}
        <div style={{ flex: 1, minWidth: 40 }}>
          {meter({ barThickness: p.barThickness || 8, showAmount: false, showTarget: false, showPercent: false, label: '' })}
        </div>
        <span style={{ flex: '0 0 auto', fontWeight: 800, fontSize: z(13), color: acc, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
        {showDonors && <span style={{ flex: '0 0 auto' }}>{donorsInline(hexA(txtColor, 0.7), 12)}</span>}
        {donorsModal}
      </div>
    );
  }

  // ── mini: tiny left-aligned meter, no card / no avatar ─────────────────
  if (variant === 'mini') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box' }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: z(8), marginBottom: z(4) }}>
            <span style={{ fontWeight: 700, fontSize: z(12.5), color: txtColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameLine}</span>
            <span style={{ fontWeight: 800, fontSize: z(12), color: acc, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{pct}%</span>
          </div>
        )}
        {meter({ barThickness: p.barThickness || 7, showAmount: false, showTarget: false, showPercent: false, label: '' })}
        {showDonors && (
          <div style={{ fontSize: z(11.5), color: hexA(txtColor, 0.65), marginTop: z(5) }}>
            {fmtVal(metric.current)}{showTarget ? ` / ${fmtVal(metric.target)}` : ''} · {donorsInline(acc, 11.5, true)}
            {donorsModal}
          </div>
        )}
      </div>
    );
  }

  // ── card (default): soft branded panel ─────────────────────────────────
  const cardBg = s.bgTransparent === false ? (s.bgColor || '#ffffff') : `linear-gradient(180deg, ${hexA(acc, 0.06)}, ${hexA(acc, 0.015)})`;
  return (
    <div style={{ width: '100%', boxSizing: 'border-box', background: cardBg, border: `1px solid ${hexA(acc, 0.22)}`, borderRadius: z(s.borderRadius ?? 18), padding: z(s.padding ?? 18), boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
      {showName && name && (
        <div style={{ display: 'flex', alignItems: 'center', gap: z(12), marginBottom: z(14) }}>
          {showAvatar && avatar(44, acc, '#fff')}
          {nameBlock(txtColor, hexA(txtColor, 0.6))}
        </div>
      )}
      {meter()}
      {donorChip(hexA(acc, 0.12), acc)}
      {sampleNote}
    </div>
  );
};

/**
 * Tiny schematic preview of a variant, for the visual picker in the editor
 * sidebar. Draws a recognizable silhouette (avatar dot, name lines, bar, chip)
 * rather than the full component — cheap to render many at once.
 */
export const VariantThumb: React.FC<{ variant: string; accent?: string }> = ({ variant, accent = '#2563eb' }) => {
  const acc = accent;
  const ink = 'rgba(17,24,39,0.4)';
  const frame: React.CSSProperties = { width: '100%', height: '100%', borderRadius: 7, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 4, padding: 7, overflow: 'hidden' };
  const dot = (size = 11, bg = acc, ring?: string) => <div style={{ width: size, height: size, borderRadius: '50%', background: bg, boxShadow: ring, flex: '0 0 auto' }} />;
  const line = (w: number | string = 26, c = ink) => <div style={{ width: w, height: 4, borderRadius: 2, background: c }} />;
  const bar = (fill = acc, track = hexA(acc, 0.2), w = '62%', h = 5) => (
    <div style={{ height: h, borderRadius: 99, background: track, overflow: 'hidden', width: '100%' }}><div style={{ width: w, height: '100%', background: fill, borderRadius: 99 }} /></div>
  );
  const chip = (bg = hexA(acc, 0.16)) => <div style={{ width: 30, height: 8, borderRadius: 99, background: bg }} />;
  const head = (avBg = acc, avRing?: string, nameC = ink) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{dot(11, avBg, avRing)}<div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{line(24, nameC)}{line(16, hexA('#111827', 0.22))}</div></div>
  );

  let body: React.ReactNode;
  switch (variant) {
    case 'spotlight':
      body = <div style={{ ...frame, background: `linear-gradient(135deg, ${acc}, ${darken(acc, 0.28)})`, gap: 6 }}>{head('rgba(255,255,255,0.85)', undefined, 'rgba(255,255,255,0.9)')}{bar('#fff', 'rgba(255,255,255,0.3)')}{chip('rgba(255,255,255,0.4)')}</div>;
      break;
    case 'stats':
      body = <div style={{ ...frame, background: '#fff', border: `1px solid ${hexA(acc, 0.18)}` }}>
        <div style={{ display: 'flex', gap: 4 }}>{[0, 1, 2].map(i => <div key={i} style={{ flex: 1, height: 16, borderRadius: 4, background: hexA(acc, i === 0 ? 0.16 : 0.07) }} />)}</div>{bar()}</div>;
      break;
    case 'hero':
      body = <div style={{ ...frame, alignItems: 'center', background: `linear-gradient(180deg, ${hexA(acc, 0.08)}, ${hexA(acc, 0.02)})`, border: `1px solid ${hexA(acc, 0.18)}`, gap: 5 }}>{dot(14)}{line(30)}<div style={{ width: 34, height: 7, borderRadius: 2, background: acc }} />{bar(acc, hexA(acc, 0.2), '62%')}</div>;
      break;
    case 'circle':
      body = <div style={{ ...frame, flexDirection: 'row', alignItems: 'center', background: '#fff', border: `1px solid ${hexA(acc, 0.18)}`, gap: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', border: `4px solid ${hexA(acc, 0.25)}`, borderTopColor: acc, borderInlineStartColor: acc, flex: '0 0 auto' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{line(28)}{line(20, hexA('#111827', 0.22))}{chip()}</div></div>;
      break;
    case 'glass':
      body = <div style={{ height: '100%', borderRadius: 7, background: `linear-gradient(135deg, ${hexA(acc, 0.35)}, ${hexA(acc, 0.12)})`, padding: 4 }}>
        <div style={{ ...frame, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.8)', height: '100%' }}>{head()}{bar()}</div></div>;
      break;
    case 'ribbon':
      body = <div style={{ ...frame, position: 'relative', background: '#fff', border: `1px solid ${hexA(acc, 0.16)}`, paddingInlineStart: 12 }}>
        <div style={{ position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, width: 5, background: acc }} />{head()}{bar()}</div>;
      break;
    case 'compact':
      body = <div style={{ ...frame, flexDirection: 'row', alignItems: 'center', background: hexA(acc, 0.06), border: `1px solid ${hexA(acc, 0.18)}`, gap: 6 }}>{dot(16)}<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>{line('70%')}{bar(acc, hexA(acc, 0.2), '60%', 4)}</div></div>;
      break;
    case 'dark':
      body = <div style={{ ...frame, background: '#1f2937', border: `1px solid ${hexA(acc, 0.45)}`, gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{dot(11, acc)}<div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{line(24, 'rgba(255,255,255,0.85)')}{line(16, 'rgba(255,255,255,0.4)')}</div></div>
        {bar(acc, 'rgba(255,255,255,0.14)')}{chip(hexA(acc, 0.35))}</div>;
      break;
    case 'pill':
      body = <div style={{ height: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', borderRadius: 99, background: hexA(acc, 0.08), border: `1px solid ${hexA(acc, 0.22)}` }}>{dot(15)}<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>{line('60%')}{bar(acc, hexA(acc, 0.2), '58%', 4)}</div>{dot(9, hexA(acc, 0.4))}</div>;
      break;
    case 'strip':
      body = <div style={{ height: '100%', display: 'flex', alignItems: 'center', gap: 5, padding: '0 6px' }}>{dot(12)}{line(18)}<div style={{ flex: 1 }}>{bar(acc, hexA(acc, 0.2), '60%', 4)}</div><div style={{ width: 12, height: 4, borderRadius: 2, background: acc }} /></div>;
      break;
    case 'mini':
      body = <div style={{ ...frame, gap: 4, justifyContent: 'center' }}>{line('55%')}{bar(acc, hexA(acc, 0.2), '60%', 4)}{line(30, hexA('#111827', 0.28))}</div>;
      break;
    case 'outline':
      body = <div style={{ ...frame, background: 'transparent', border: `1.5px solid ${hexA(acc, 0.55)}` }}>{head('transparent', `inset 0 0 0 2px ${acc}`)}{bar()}{chip()}</div>;
      break;
    case 'plain':
      body = <div style={{ ...frame, alignItems: 'center', justifyContent: 'center', background: 'transparent', gap: 6 }}>{line(40)}{bar()}<div style={{ width: 30, height: 8, borderRadius: 99, background: hexA(acc, 0.16) }} /></div>;
      break;
    default: // card
      body = <div style={{ ...frame, background: `linear-gradient(180deg, ${hexA(acc, 0.09)}, ${hexA(acc, 0.02)})`, border: `1px solid ${hexA(acc, 0.22)}` }}>{head()}{bar()}{chip()}</div>;
  }
  return <div style={{ width: '100%', height: '100%' }}>{body}</div>;
};

export default AmbassadorStatsCard;
